import { useState } from 'react'
import { Mail, Heart } from 'lucide-react'
import { LegalModal, type LegalDoc } from './LegalModal'
import { GooglePlayBadge, AppStoreBadge } from './StoreBadges'

// Mirrors the in-page sections (see Header nav) so the footer speaks the same
// language as the rest of the page.
const exploreLinks = [
  { href: '#products', label: 'Products' },
  { href: '#features', label: 'Features' },
  { href: '#fuzzy', label: 'Fuzzy Search' },
  { href: '#tester', label: 'Become a Tester' },
]

export function Footer() {
  const [openDoc, setOpenDoc] = useState<LegalDoc | null>(null)

  return (
    <footer className="relative overflow-hidden bg-[#0B1A2C] text-slate-300">
      {/* Brand-gradient seam + soft glow — echoes the hero's radial accents. */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#4C9AFF]/45 to-transparent" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-[radial-gradient(60%_120%_at_50%_0%,rgba(76,154,255,0.10),rgba(88,86,214,0.06),transparent_72%)]"
      />

      <div className="relative mx-auto max-w-6xl px-5 py-14 md:py-16">
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-12 md:gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-5">
            <a
              href="#top"
              className="inline-flex items-center"
              aria-label="BimaUncle home"
            >
              <img
                src="/bimauncle-logo.png"
                alt="BimaUncle"
                className="h-[7.5rem] w-auto"
              />
            </a>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              India&apos;s insurance comparison platform — compare quotes across
              motor, health, fire and more, and find the right coverage in
              seconds.
            </p>
            <a
              href="mailto:hello@bimauncle.com"
              className="group mt-6 inline-flex items-center gap-2.5 text-sm text-slate-300 transition-colors hover:text-white"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-pill bg-white/5 text-[#4C9AFF] ring-1 ring-white/10 transition-colors group-hover:bg-white/10">
                <Mail size={15} />
              </span>
              hello@bimauncle.com
            </a>
          </div>

          {/* Get the app */}
          <div className="col-span-2 md:col-span-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Get the app
            </h4>
            <div className="mt-4 flex flex-col items-start gap-2.5">
              <GooglePlayBadge height={44} />
              <AppStoreBadge height={44} />
            </div>
          </div>

          {/* Explore */}
          <nav className="col-span-1 md:col-span-2" aria-label="Explore">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Explore
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              {exploreLinks.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="text-slate-400 transition-colors hover:text-white"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Legal */}
          <nav className="col-span-1 md:col-span-2" aria-label="Legal">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Legal &amp; support
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a
                  href="/support"
                  className="text-slate-400 transition-colors hover:text-white"
                >
                  Support
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setOpenDoc('privacy')}
                  className="text-slate-400 transition-colors hover:text-white"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setOpenDoc('terms')}
                  className="text-slate-400 transition-colors hover:text-white"
                >
                  Terms &amp; Conditions
                </button>
              </li>
              <li>
                <a
                  href="/cookie-policy"
                  className="text-slate-400 transition-colors hover:text-white"
                >
                  Cookie Policy
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-5 text-xs text-slate-500 sm:flex-row">
          <span>© 2026 BimaUncle. All rights reserved.</span>
          <span className="inline-flex items-center gap-1.5">
            Made in India
            <Heart size={12} className="fill-[#4C9AFF] text-[#4C9AFF]" />
          </span>
        </div>
      </div>

      <LegalModal doc={openDoc} onClose={() => setOpenDoc(null)} />
    </footer>
  )
}

